import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Plus, Trash2, Image as ImageIcon, Save, Users, Utensils, ShieldAlert, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const emptyProfessional = { name: "", role: "", specialty: "", avatar_url: "" };
const emptyPlan = { name: "", monthly_price: null, includes: [], description: "", room_photos: [] };
const GALLERY_MAX = 15;
const PLAN_PHOTOS_MIN = 3;
const PLAN_PHOTOS_MAX = 8;

// Remove strings vazias do payload — Pydantic v2 é estrito com EmailStr=""
// e derrubava o save em tela branca (bug reportado).
const cleanPayload = (obj) => {
  const out = {};
  for (const [k, v] of Object.entries(obj || {})) {
    if (v === "" || v === null || v === undefined) continue;
    out[k] = v;
  }
  return out;
};

const ClinicProfileEdit = () => {
  const { user } = useAuth();
  const { clinicId } = useParams();          // presente = modo admin
  const nav = useNavigate();
  const isAdminMode = !!clinicId && user?.role === "admin";
  const getUrl = isAdminMode ? `/admin/clinics/${clinicId}` : "/clinic/me";
  const patchUrl = isAdminMode ? `/admin/clinics/${clinicId}` : "/clinic/me/profile";
  const uploadUrl = isAdminMode ? `/admin/clinics/${clinicId}/upload-image` : "/clinic/me/upload-image";

  const [clinic, setClinic] = useState(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const galleryRef = useRef(null);
  const heroRef = useRef(null);
  const logoRef = useRef(null);
  const planPhotoRefs = useRef({});

  useEffect(() => {
    api.get(getUrl)
      .then(({ data }) => setClinic(isAdminMode ? data : (data.clinic || {})))
      .catch(() => toast.error("Erro ao carregar perfil"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId]);

  const patch = async () => {
    setBusy(true);
    try {
      const payload = cleanPayload(clinic);
      await api.patch(patchUrl, payload);
      toast.success(isAdminMode ? "Alterações salvas (modo admin)" : "Perfil salvo com sucesso");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = "Erro ao salvar";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail) && detail.length) {
        msg = detail.map((d) => `${(d.loc || []).slice(-1)[0]}: ${d.msg}`).join(" · ");
      } else if (err?.message) msg = err.message;
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!isAdminMode) return;
    const confirmName = window.prompt(
      `ATENÇÃO: você está prestes a EXCLUIR permanentemente a unidade "${clinic.name}".\n\n` +
      `Isso remove: doc da clínica, usuário da clínica vinculado e sua candidatura.\n` +
      `Casos ATIVOS bloqueiam a exclusão.\n\n` +
      `Para confirmar, digite EXATAMENTE o nome da unidade:`
    );
    if (!confirmName) return;
    if (confirmName.trim() !== (clinic.name || "").trim()) {
      toast.error("Nome não confere — exclusão cancelada.");
      return;
    }
    setDeleting(true);
    try {
      const { data } = await api.delete(`/admin/clinics/${clinicId}`);
      toast.success(`Unidade excluída. ${data.users_removed || 0} usuário(s) e ${data.applications_removed || 0} candidatura(s) removidos em cascata.`);
      nav("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao excluir unidade");
    } finally { setDeleting(false); }
  };

  const upload = async (e, target, extra) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(uploadUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (target === "hero") setClinic({ ...clinic, hero_image: data.url });
      else if (target === "logo") setClinic({ ...clinic, logo_url: data.url });
      else if (target === "gallery") {
        const current = clinic.gallery || [];
        if (current.length >= GALLERY_MAX) { toast.error(`Máximo de ${GALLERY_MAX} fotos na galeria`); return; }
        setClinic({ ...clinic, gallery: [...current, data.url] });
      } else if (target === "plan_room") {
        // extra = índice do plano
        const plans = [...(clinic.plans || [])];
        const p = plans[extra] || { ...emptyPlan };
        const photos = p.room_photos || [];
        if (photos.length >= PLAN_PHOTOS_MAX) { toast.error(`Máximo de ${PLAN_PHOTOS_MAX} fotos por plano`); return; }
        plans[extra] = { ...p, room_photos: [...photos, data.url] };
        setClinic({ ...clinic, plans });
      }
      toast.success("Imagem enviada");
    } catch { toast.error("Erro no upload da imagem"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  if (!clinic) return <div className="p-10 text-slate-500">Carregando…</div>;

  const set = (k, v) => setClinic({ ...clinic, [k]: v });
  const arr = (k) => clinic[k] || [];

  return (
    <div className="max-w-4xl mx-auto px-6 py-10 space-y-6">
      {isAdminMode && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 flex items-start gap-3" data-testid="admin-mode-banner">
          <ShieldAlert className="w-6 h-6 text-amber-700 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-bold text-amber-900">Modo Administrador · auditoria</div>
            <div className="text-xs text-amber-800 mt-0.5">
              Você está editando <strong>como admin</strong> o cadastro da unidade. Todas as alterações e uploads são feitas em nome da clínica e registradas no audit log.
            </div>
          </div>
          <button onClick={() => nav("/admin")} data-testid="admin-back-btn"
            className="text-xs inline-flex items-center gap-1 px-3 py-1.5 border border-amber-300 text-amber-800 rounded-md hover:bg-amber-100 shrink-0">
            <ArrowLeft className="w-3 h-3" /> Voltar
          </button>
        </div>
      )}

      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">
          {isAdminMode ? "Auditar unidade" : "Perfil da Unidade"}
        </div>
        <h1 className="font-display font-bold text-3xl text-[#0B2B62] mt-1 flex items-center gap-3 flex-wrap">
          {clinic.name}
          {clinic.code && <span className="text-sm font-mono px-2 py-0.5 rounded-md bg-slate-100 text-slate-600">{clinic.code}</span>}
          {clinic.is_public === false
            ? <span className="text-[10px] uppercase tracking-widest bg-slate-100 text-slate-600 px-2 py-1 rounded-full inline-flex items-center gap-1"><EyeOff className="w-3 h-3" /> Oculta</span>
            : <span className="text-[10px] uppercase tracking-widest bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full inline-flex items-center gap-1"><Eye className="w-3 h-3" /> Pública</span>}
        </h1>
        {isAdminMode && clinic.city && (
          <p className="text-sm text-slate-500 mt-1">📍 {clinic.city}, {clinic.state} · <a href={`/unidades/${clinic.id}`} target="_blank" rel="noreferrer" className="text-[#0B2B62] hover:underline font-semibold">ver página pública ↗</a></p>
        )}
      </div>

      {/* Logotipo — usado no cabeçalho do recibo da família */}
      <Section title="Logotipo da unidade" icon={ImageIcon}>
        <div className="flex items-center gap-4 flex-wrap">
          {clinic.logo_url ? (
            <img src={clinic.logo_url} alt="" className="h-24 w-auto max-w-[240px] object-contain bg-white rounded-lg border border-slate-200 p-2" data-testid="clinic-logo-preview" />
          ) : (
            <div className="h-24 w-48 bg-slate-100 rounded-lg flex items-center justify-center text-slate-400 text-xs text-center px-3">Sem logo — o recibo mostrará o nome em texto</div>
          )}
          <div className="flex flex-col gap-2">
            <button type="button" onClick={() => logoRef.current?.click()} disabled={uploading} data-testid="clinic-logo-btn"
              className="px-3 py-2 border border-slate-300 rounded-md text-sm hover:bg-slate-50">
              {clinic.logo_url ? "Trocar logotipo" : "Enviar logotipo"}
            </button>
            {clinic.logo_url && (
              <button type="button" onClick={() => set("logo_url", "")} data-testid="clinic-logo-remove"
                className="px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-md">Remover</button>
            )}
          </div>
          <input ref={logoRef} hidden type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => upload(e, "logo")} />
          <p className="text-xs text-slate-500 flex-1 min-w-[200px] max-w-md">
            Aparece no <b>topo do recibo de matrícula</b> que a família recebe. Ideal: PNG com fundo transparente ou branco, ~800×300px.
          </p>
        </div>
      </Section>

      {/* Hero image */}
      <Section title="Imagem principal" icon={ImageIcon}>
        <div className="flex items-center gap-4">
          {clinic.hero_image ? (
            <img src={clinic.hero_image} alt="" className="w-64 h-40 object-cover rounded-lg border border-slate-200" data-testid="clinic-hero-preview" />
          ) : (
            <div className="w-64 h-40 bg-slate-100 rounded-lg flex items-center justify-center text-slate-400">Sem imagem</div>
          )}
          <button type="button" onClick={() => heroRef.current?.click()} disabled={uploading} data-testid="clinic-hero-btn"
            className="px-3 py-2 border border-slate-300 rounded-md text-sm hover:bg-slate-50">Trocar imagem</button>
          <input ref={heroRef} hidden type="file" accept="image/*" onChange={(e) => upload(e, "hero")} />
        </div>
      </Section>

      {/* Galeria */}
      <Section title="Galeria da unidade" icon={ImageIcon}>
        <div className="mb-3 flex items-center justify-between text-xs text-slate-500">
          <span>Áreas comuns, fachada, refeitório, jardim etc.</span>
          <span data-testid="gallery-counter">
            {arr("gallery").length} / {GALLERY_MAX} fotos
          </span>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
          {arr("gallery").map((url, i) => (
            <div key={i} className="relative aspect-square bg-slate-100 rounded-md overflow-hidden" data-testid={`clinic-gallery-${i}`}>
              <img src={url} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => set("gallery", arr("gallery").filter((_, j) => j !== i))}
                className="absolute top-1 right-1 bg-red-600 text-white p-1 rounded-full hover:bg-red-700">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
          {arr("gallery").length < GALLERY_MAX && (
            <button type="button" onClick={() => galleryRef.current?.click()} data-testid="clinic-gallery-add"
              className="aspect-square border-2 border-dashed border-slate-300 rounded-md hover:border-[#0B2B62] flex flex-col items-center justify-center text-slate-500 text-xs">
              <Plus className="w-6 h-6 mb-1" /> Adicionar
            </button>
          )}
          <input ref={galleryRef} hidden type="file" accept="image/*" onChange={(e) => upload(e, "gallery")} />
        </div>
      </Section>

      {/* Basicos */}
      <Section title="Informações">
        <div className="grid md:grid-cols-2 gap-3">
          <Fld label="Descrição" value={clinic.description} onChange={(v) => set("description", v)} testId="clinic-description" full />
          <Fld label="Endereço" value={clinic.address} onChange={(v) => set("address", v)} testId="clinic-address" full />
          <Fld label="Website" value={clinic.website} onChange={(v) => set("website", v)} testId="clinic-website" />
          <Fld label="WhatsApp" value={clinic.contact_whatsapp} onChange={(v) => set("contact_whatsapp", v)} testId="clinic-wpp" />
          <Fld label="Horário de funcionamento" value={clinic.hours_operation} onChange={(v) => set("hours_operation", v)} testId="clinic-hours" />
          <Fld label="Vagas disponíveis" type="number" value={clinic.beds_available} onChange={(v) => set("beds_available", parseInt(v || 0))} testId="clinic-beds" />
        </div>
      </Section>

      {/* Features / refeições / atividades */}
      <Section title="O que a unidade oferece" icon={Utensils}>
        <div className="grid md:grid-cols-2 gap-3">
          <Fld label="Refeições ao dia" type="number" value={clinic.meals_per_day} onChange={(v) => set("meals_per_day", parseInt(v || 0))} testId="clinic-meals" />
        </div>

        <div className="mt-4">
          <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Tipo de estabelecimento</label>
          <p className="text-[11px] text-slate-500 mt-0.5">Marque o formato que melhor descreve a unidade</p>
          <div className="flex flex-wrap gap-1.5 mt-2" data-testid="establishment-picker">
            {COMMON_ESTABLISHMENT.map((opt) => {
              const active = (clinic.establishment_type || "") === opt;
              return (
                <button
                  type="button"
                  key={opt}
                  onClick={() => set("establishment_type", active ? "" : opt)}
                  data-testid={`establishment-opt-${opt.slice(0, 12).replace(/\s+/g, "-").toLowerCase()}`}
                  className={`text-xs px-2.5 py-1.5 rounded-full border font-semibold transition-colors ${
                    active
                      ? "bg-[#0B2B62] border-[#0B2B62] text-white hover:bg-[#0A2456]"
                      : "bg-white border-slate-300 text-slate-600 hover:border-[#0B2B62] hover:text-[#0B2B62]"
                  }`}
                >
                  {active ? "✓ " : ""}{opt}
                </button>
              );
            })}
          </div>
        </div>

        <PickerPlusChips
          label="Público-alvo"
          hint="Marque os públicos que a unidade atende"
          options={COMMON_AUDIENCE}
          values={arr("audience")}
          onChange={(v) => set("audience", v)}
          testIdPrefix="audience"
        />

        <PickerPlusChips
          label="Modalidades de internação atendidas"
          hint="Marque as modalidades oferecidas por esta unidade"
          options={COMMON_MODALITIES}
          values={arr("modality")}
          onChange={(v) => set("modality", v)}
          testIdPrefix="modality"
          allowCustom={false}
        />

        <PickerPlusChips
          label="Estrutura & diferenciais"
          hint="Marque o que sua unidade oferece — use o campo abaixo para itens específicos"
          options={COMMON_FEATURES}
          values={arr("features")}
          onChange={(v) => set("features", v)}
          testIdPrefix="feature"
        />

        <PickerPlusChips
          label="Atividades e programa terapêutico"
          hint="Marque as práticas oferecidas — use o campo abaixo para itens específicos"
          options={COMMON_ACTIVITIES}
          values={arr("activities")}
          onChange={(v) => set("activities", v)}
          testIdPrefix="activity"
        />

        <PickerPlusChips
          label="Acreditações e órgãos"
          hint="Marque as certificações que a unidade possui"
          options={COMMON_ACCREDITATIONS}
          values={arr("accreditations")}
          onChange={(v) => set("accreditations", v)}
          testIdPrefix="accred"
        />
      </Section>

      {/* Profissionais */}
      <Section title="Profissionais que atendem" icon={Users}>
        {arr("professionals").map((p, i) => (
          <div key={i} className="grid md:grid-cols-4 gap-2 mb-2 p-2 border border-slate-200 rounded" data-testid={`prof-${i}`}>
            <input placeholder="Nome" value={p.name} onChange={(e) => set("professionals", arr("professionals").map((x, j) => j === i ? { ...x, name: e.target.value } : x))} className="px-2 py-1 border rounded text-sm" />
            <input placeholder="Cargo (ex: Psicólogo)" value={p.role} onChange={(e) => set("professionals", arr("professionals").map((x, j) => j === i ? { ...x, role: e.target.value } : x))} className="px-2 py-1 border rounded text-sm" />
            <input placeholder="Especialidade" value={p.specialty} onChange={(e) => set("professionals", arr("professionals").map((x, j) => j === i ? { ...x, specialty: e.target.value } : x))} className="px-2 py-1 border rounded text-sm" />
            <button type="button" onClick={() => set("professionals", arr("professionals").filter((_, j) => j !== i))} className="text-red-500 text-xs hover:underline"><Trash2 className="w-3 h-3 inline" /> remover</button>
          </div>
        ))}
        <button type="button" onClick={() => set("professionals", [...arr("professionals"), { ...emptyProfessional }])} data-testid="prof-add"
          className="text-sm px-3 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50"><Plus className="w-3.5 h-3.5 inline" /> Adicionar profissional</button>
      </Section>

      {/* Planos */}
      <Section title="Planos e valores">
        <p className="text-xs text-slate-500 mb-4">
          Cada plano deve ter <strong>{PLAN_PHOTOS_MIN} a {PLAN_PHOTOS_MAX} fotos</strong> do quarto/acomodação
          para que a família saiba exatamente onde o paciente vai ficar.
        </p>
        {arr("plans").map((p, i) => {
          const photos = p.room_photos || [];
          const photosOk = photos.length >= PLAN_PHOTOS_MIN;
          return (
            <div key={i} className="mb-4 p-4 border border-slate-200 rounded-lg bg-slate-50/40" data-testid={`plan-${i}`}>
              <div className="grid md:grid-cols-3 gap-2 mb-2">
                <input placeholder="Nome do plano (ex: Standard, VIP)" value={p.name} onChange={(e) => set("plans", arr("plans").map((x, j) => j === i ? { ...x, name: e.target.value } : x))} className="px-2 py-1.5 border rounded text-sm" data-testid={`plan-${i}-name`} />
                <input placeholder="Valor mensal (R$)" type="number" value={p.monthly_price || ""} onChange={(e) => set("plans", arr("plans").map((x, j) => j === i ? { ...x, monthly_price: parseFloat(e.target.value) || null } : x))} className="px-2 py-1.5 border rounded text-sm" data-testid={`plan-${i}-price`} />
                <button type="button" onClick={() => set("plans", arr("plans").filter((_, j) => j !== i))} className="text-red-600 text-xs hover:underline text-left" data-testid={`plan-${i}-remove`}><Trash2 className="w-3 h-3 inline" /> remover plano</button>
              </div>
              <textarea placeholder="Descrição do plano" value={p.description} onChange={(e) => set("plans", arr("plans").map((x, j) => j === i ? { ...x, description: e.target.value } : x))} rows={2} className="w-full px-2 py-1.5 border rounded text-sm mb-3" data-testid={`plan-${i}-desc`} />
              <ChipList label="Inclui" values={p.includes || []} onChange={(v) => set("plans", arr("plans").map((x, j) => j === i ? { ...x, includes: v } : x))} testIdPrefix={`plan-${i}-inc`} inline />

              {/* Fotos do quarto/acomodação deste plano */}
              <div className="mt-4 pt-4 border-t border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs uppercase tracking-widest text-slate-600 font-semibold flex items-center gap-1.5">
                    <ImageIcon className="w-3.5 h-3.5" /> Fotos do quarto deste plano
                  </label>
                  <span className={`text-xs font-semibold ${photosOk ? "text-green-700" : "text-amber-700"}`} data-testid={`plan-${i}-photos-counter`}>
                    {photos.length} / {PLAN_PHOTOS_MAX} · mín {PLAN_PHOTOS_MIN}
                  </span>
                </div>
                <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                  {photos.map((url, k) => (
                    <div key={k} className="relative aspect-square bg-slate-200 rounded-md overflow-hidden" data-testid={`plan-${i}-photo-${k}`}>
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => set("plans", arr("plans").map((x, j) => j === i ? { ...x, room_photos: photos.filter((_, m) => m !== k) } : x))}
                        className="absolute top-1 right-1 bg-red-600 text-white p-1 rounded-full hover:bg-red-700">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {photos.length < PLAN_PHOTOS_MAX && (
                    <button type="button" onClick={() => planPhotoRefs.current[i]?.click()} data-testid={`plan-${i}-photo-add`}
                      className="aspect-square border-2 border-dashed border-slate-300 rounded-md hover:border-[#0B2B62] flex flex-col items-center justify-center text-slate-500 text-xs">
                      <Plus className="w-5 h-5 mb-0.5" /> foto
                    </button>
                  )}
                  <input
                    ref={(el) => { planPhotoRefs.current[i] = el; }}
                    hidden type="file" accept="image/*"
                    onChange={(e) => upload(e, "plan_room", i)}
                  />
                </div>
                {!photosOk && (
                  <p className="text-[11px] text-amber-700 mt-1.5" data-testid={`plan-${i}-photos-warn`}>
                    Faltam {PLAN_PHOTOS_MIN - photos.length} foto(s) para atingir o mínimo obrigatório.
                  </p>
                )}
              </div>
            </div>
          );
        })}
        <button type="button" onClick={() => set("plans", [...arr("plans"), { ...emptyPlan }])} data-testid="plan-add"
          className="text-sm px-3 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50"><Plus className="w-3.5 h-3.5 inline" /> Adicionar plano</button>
      </Section>

      {clinic.is_certified && (
        <section className="bg-white border border-slate-200 rounded-xl p-5">
          <h2 className="font-display font-semibold text-lg text-[#0B2B62] mb-2">Certificado RAF Direct</h2>
          <p className="text-sm text-slate-600 mb-3">Baixe o PDF assinado com QR code para imprimir e exibir na recepção da unidade.</p>
          <a data-testid="clinic-cert-pdf-btn"
            href={`${process.env.REACT_APP_BACKEND_URL}/api/clinics/${clinic.id}/certificate.pdf`}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white font-semibold px-4 py-2 rounded-md text-sm">
            Baixar Certificado (PDF)
          </a>
        </section>
      )}

      <button onClick={patch} disabled={busy} data-testid="clinic-profile-save"
        className="inline-flex items-center gap-2 bg-[#0B2B62] text-white font-semibold px-5 py-2.5 rounded-md text-sm disabled:opacity-60">
        <Save className="w-4 h-4" /> {busy ? "Salvando…" : "Salvar perfil"}
      </button>

      {isAdminMode && (
        <section className="bg-red-50 border-2 border-red-300 rounded-xl p-5 mt-8" data-testid="admin-danger-zone">
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-6 h-6 text-red-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-red-900">Zona de perigo — exclusão da unidade</div>
              <div className="text-xs text-red-800 mt-1 leading-relaxed">
                Use apenas se a unidade violar regras da rede RAF (ex: expor nome/logo em fotos, dados falsos, descumprimento reiterado).
                A ação é <strong>permanente</strong>: remove o cadastro, o usuário da clínica vinculado e a candidatura original.
                Casos <strong>ativos</strong> bloqueiam a exclusão.
              </div>
              <button
                onClick={remove}
                disabled={deleting}
                data-testid="admin-clinic-delete-btn"
                className="mt-4 inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold px-4 py-2 rounded-md text-sm disabled:opacity-60"
              >
                <Trash2 className="w-4 h-4" /> {deleting ? "Excluindo…" : "Excluir unidade permanentemente"}
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

const Section = ({ title, icon: Icon, children }) => (
  <section className="bg-white border border-slate-200 rounded-xl p-5">
    <h2 className="font-display font-semibold text-lg text-[#0B2B62] mb-3 flex items-center gap-2">
      {Icon && <Icon className="w-4 h-4 text-slate-400" />} {title}
    </h2>
    {children}
  </section>
);

const Fld = ({ label, value, onChange, type = "text", full, testId }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</label>
    <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-[#0B2B62]" />
  </div>
);

const ChipList = ({ label, values, onChange, testIdPrefix, inline }) => {
  const [input, setInput] = useState("");
  return (
    <div className={inline ? "" : "mt-3"}>
      <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</label>
      <div className="flex flex-wrap gap-1.5 mt-1">
        {values.map((v, i) => (
          <span key={i} className="text-xs bg-slate-100 rounded-full px-2 py-1 inline-flex items-center gap-1" data-testid={`${testIdPrefix}-${i}`}>
            {v}
            <button type="button" onClick={() => onChange(values.filter((_, j) => j !== i))} className="text-slate-500 hover:text-red-600">×</button>
          </span>
        ))}
        <input value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && input.trim()) { e.preventDefault(); onChange([...values, input.trim()]); setInput(""); }}}
          placeholder="digite e pressione Enter"
          className="text-xs px-2 py-1 border border-slate-300 rounded-md min-w-[180px]" />
      </div>
    </div>
  );
};

// ----- Sugestões pré-definidas para a unidade marcar rapidamente -----
const COMMON_ESTABLISHMENT = [
  "Comunidade Terapêutica", "Clínica", "Hospital", "Consultório", "Outros",
];
const COMMON_AUDIENCE = [
  "Masculino", "Feminino", "Mista", "Adolescente", "Idosos", "Outros",
];
const COMMON_MODALITIES = [
  "Voluntária", "Involuntária", "Compulsória",
];
const COMMON_FEATURES = [
  "Área externa / jardim", "Piscina", "Academia", "Sala de meditação",
  "Ambiente cercado por natureza", "Enfermaria 24h", "Quartos privativos",
  "Quartos compartilhados", "Ar-condicionado", "Wi-Fi", "Refeitório próprio",
  "Sala de TV / lazer", "Biblioteca", "Capela / espaço espiritual",
  "Sala de artes", "Sala de música", "Horta terapêutica",
];
const COMMON_ACTIVITIES = [
  "Terapia individual", "Terapia em grupo", "Terapia familiar",
  "AA / NA (12 Passos)", "Arteterapia", "Musicoterapia",
  "Terapia Cognitivo-Comportamental (TCC)", "Atividade física orientada",
  "Yoga & meditação", "Oficinas ocupacionais", "Grupos espirituais",
  "Prevenção de recaída", "Reinserção social", "Pós-tratamento / follow-up",
  "Modelo Minnesota", "Terapia racional-emotiva",
];
const COMMON_ACCREDITATIONS = [
  "CNES ativo", "Vigilância Sanitária", "Alvará da Prefeitura",
  "CRP registrado", "CRM registrado", "CEBAS", "ANVISA", "Corpo de Bombeiros",
  "ONA", "CREAS/CRAS",
];

// Combina checkboxes de opções comuns + input livre pra itens custom
const PickerPlusChips = ({ label, hint, options, values, onChange, testIdPrefix, allowCustom = true }) => {
  const [input, setInput] = useState("");
  const has = (opt) => values.includes(opt);
  const toggle = (opt) => onChange(has(opt) ? values.filter((v) => v !== opt) : [...values, opt]);
  const customs = values.filter((v) => !options.includes(v));
  return (
    <div className="mt-4">
      <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</label>
      {hint && <p className="text-[11px] text-slate-500 mt-0.5">{hint}</p>}
      <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`${testIdPrefix}-picker`}>
        {options.map((opt) => {
          const active = has(opt);
          return (
            <button
              type="button"
              key={opt}
              onClick={() => toggle(opt)}
              data-testid={`${testIdPrefix}-opt-${opt.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}
              className={`text-xs px-2.5 py-1.5 rounded-full border font-semibold transition-colors ${
                active
                  ? "bg-[#0B2B62] border-[#0B2B62] text-white hover:bg-[#0A2456]"
                  : "bg-white border-slate-300 text-slate-600 hover:border-[#0B2B62] hover:text-[#0B2B62]"
              }`}
            >
              {active ? "✓ " : ""}{opt}
            </button>
          );
        })}
      </div>
      {allowCustom && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-customs`}>
          {customs.map((v, i) => (
            <span key={v} className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-full px-2 py-1 inline-flex items-center gap-1" data-testid={`${testIdPrefix}-${i}`}>
              {v}
              <button type="button" onClick={() => onChange(values.filter((x) => x !== v))} className="text-emerald-700 hover:text-red-600">×</button>
            </span>
          ))}
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && input.trim()) { e.preventDefault(); onChange([...values, input.trim()]); setInput(""); } }}
            placeholder="+ adicionar item específico (Enter)"
            className="text-xs px-2 py-1 border border-slate-300 rounded-md min-w-[220px] flex-1"
            data-testid={`${testIdPrefix}-add-custom`}
          />
        </div>
      )}
    </div>
  );
};

export default ClinicProfileEdit;
